import { useEffect, useState } from "react";
import { useNotify, useRefresh, useUpdate } from "ra-core";
import {
  AlertTriangle,
  ArrowRight,
  Check,
  Copy,
  ExternalLink,
  Loader2,
  Radar,
  Radio,
  Send,
  Sparkles,
  Star,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getSupabaseClient } from "../providers/supabase/supabase";
import type { Contact, JobChangeAlert } from "../types";

export function ChampionStarToggle({
  contact,
}: {
  contact: Contact;
}) {
  const [update] = useUpdate<Contact>();
  const notify = useNotify();
  const isChampion = Boolean(contact.is_champion);

  const toggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const nextVal = !isChampion;
    update(
      "contacts",
      {
        id: contact.id,
        data: { is_champion: nextVal },
        previousData: contact,
      },
      {
        mutationMode: "optimistic",
        onSuccess: () => {
          notify(
            nextVal
              ? `⭐ Tagged ${contact.first_name || "contact"} as Champion for Job Change Radar`
              : `Removed champion tag from ${contact.first_name || "contact"}`,
            { type: "info" },
          );
        },
      },
    );
  };

  return (
    <button
      type="button"
      onClick={toggle}
      title={isChampion ? "Champion (Tracked on Radar)" : "Mark as Champion"}
      className={`size-6 inline-flex items-center justify-center rounded-md transition-colors cursor-pointer ${
        isChampion
          ? "text-amber-500 hover:text-amber-600 bg-amber-500/10"
          : "text-muted-foreground/50 hover:text-amber-500 hover:bg-muted"
      }`}
    >
      <Star
        className={`size-3.5 ${isChampion ? "fill-current" : ""}`}
      />
    </button>
  );
}

export function JobChangeRadarButton() {
  const [open, setOpen] = useState(false);
  const [alertCount, setAlertCount] = useState<number>(0);

  useEffect(() => {
    async function loadUnackCount() {
      try {
        const supabase = getSupabaseClient();
        const { count } = await supabase
          .from("job_change_alerts")
          .select("id", { count: "exact", head: true })
          .eq("acknowledged", false);
        setAlertCount(count || 0);
      } catch {
        // Non-critical
      }
    }
    loadUnackCount();
  }, [open]);

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="cursor-pointer gap-1.5 border-rose-500/30 bg-rose-500/5 hover:bg-rose-500/10 text-rose-700 dark:text-rose-300 relative"
      >
        <Radar className="size-3.5 text-rose-500" />
        <span>Job Change Radar</span>
        {alertCount > 0 && (
          <span className="flex size-4 items-center justify-center rounded-full bg-rose-600 text-[10px] font-bold text-white ml-0.5 animate-pulse">
            {alertCount}
          </span>
        )}
      </Button>

      {open && (
        <JobChangeRadarModal open={open} onOpenChange={setOpen} />
      )}
    </>
  );
}

export function JobChangeRadarModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const notify = useNotify();
  const refresh = useRefresh();
  const [alerts, setAlerts] = useState<JobChangeAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [copiedId, setCopiedId] = useState<number | null>(null);

  const loadAlerts = async () => {
    try {
      setLoading(true);
      const supabase = getSupabaseClient();
      const { data, error } = await supabase
        .from("job_change_alerts")
        .select("*, contacts(id, first_name, last_name, linkedin_url, title)")
        .order("detected_at", { ascending: false })
        .limit(20);

      if (!error && data) {
        setAlerts(
          data.map((row: any) => ({
            ...row,
            contact_name: `${row.contacts?.first_name || ""} ${row.contacts?.last_name || ""}`.trim(),
            linkedin_url: row.contacts?.linkedin_url,
          })),
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadAlerts();
    }
  }, [open]);

  const handleScanNow = async () => {
    setScanning(true);
    try {
      const supabase = getSupabaseClient();
      const { data, error } = await supabase.functions.invoke(
        "job-change-radar",
        {
          body: { limit: 20 },
        },
      );

      if (error) {
        throw error;
      }

      notify(
        `Job Change Radar complete: Scanned ${data?.scanned_count || 0} prospects (${data?.job_changes_found || 0} career moves detected)`,
        { type: "info" },
      );
      await loadAlerts();
      refresh();
    } catch (err) {
      notify(
        `Radar scan failed: ${err instanceof Error ? err.message : "Unknown error"}`,
        { type: "error" },
      );
    } finally {
      setScanning(false);
    }
  };

  const handleAcknowledge = async (alertId: number) => {
    try {
      const supabase = getSupabaseClient();
      await supabase
        .from("job_change_alerts")
        .update({ acknowledged: true })
        .eq("id", alertId);

      setAlerts((prev) =>
        prev.map((a) => (a.id === alertId ? { ...a, acknowledged: true } : a)),
      );
      notify("Alert acknowledged", { type: "info" });
      refresh();
    } catch {
      notify("Failed to acknowledge alert", { type: "error" });
    }
  };

  const handleCopyAndDM = (alert: JobChangeAlert) => {
    if (alert.ai_reengagement_hook) {
      navigator.clipboard.writeText(alert.ai_reengagement_hook);
      setCopiedId(alert.id);
      setTimeout(() => setCopiedId(null), 2000);
    }
    if (alert.linkedin_url) {
      window.open(alert.linkedin_url, "_blank", "noopener,noreferrer");
    }
    notify("Re-engagement hook copied & LinkedIn profile opened!", {
      type: "info",
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between pr-6">
            <div className="flex items-center gap-2">
              <div className="flex size-7 items-center justify-center rounded-lg bg-rose-500/15 text-rose-600 dark:text-rose-400">
                <Radar className="size-4" />
              </div>
              <div>
                <DialogTitle className="text-lg font-bold">
                  Job Change Radar & Champion Tracker
                </DialogTitle>
                <DialogDescription className="text-xs">
                  Monitors prospects and past champions for LinkedIn career moves to trigger warm re-engagement.
                </DialogDescription>
              </div>
            </div>

            <Button
              type="button"
              size="sm"
              disabled={scanning}
              onClick={handleScanNow}
              className="gap-1.5 bg-rose-600 hover:bg-rose-700 text-white cursor-pointer shrink-0"
            >
              {scanning ? (
                <>
                  <Loader2 className="size-3.5 animate-spin" />
                  <span>Scanning...</span>
                </>
              ) : (
                <>
                  <Radio className="size-3.5" />
                  <span>Scan Radar Now</span>
                </>
              )}
            </Button>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-3 py-2 pr-1">
          {loading ? (
            <div className="py-12 text-center text-xs text-muted-foreground flex items-center justify-center gap-2">
              <Loader2 className="size-4 animate-spin text-rose-500" />
              <span>Checking live signals...</span>
            </div>
          ) : alerts.length === 0 ? (
            <div className="py-12 text-center rounded-xl border border-dashed p-6">
              <Radar className="size-8 text-muted-foreground mx-auto mb-2 opacity-50" />
              <p className="text-sm font-semibold text-foreground">
                No Job Change Alerts Yet
              </p>
              <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">
                Tag your champions with ⭐ on the Contacts page, or click "Scan Radar Now" above to run an automated signal check across your pipeline.
              </p>
            </div>
          ) : (
            alerts.map((alert) => (
              <div
                key={alert.id}
                className={`rounded-xl border p-4 space-y-3 transition-all ${
                  alert.acknowledged
                    ? "border-border/60 bg-muted/20 opacity-75"
                    : "border-rose-500/30 bg-rose-500/5 shadow-xs"
                }`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm text-foreground">
                      {alert.contact_name || `Contact #${alert.contact_id}`}
                    </span>
                    <Badge
                      variant="outline"
                      className="border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300 text-[10px]"
                    >
                      <AlertTriangle className="size-2.5 mr-1" />
                      Job Change Detected
                    </Badge>
                  </div>

                  <span className="text-[10px] text-muted-foreground">
                    {new Date(alert.detected_at).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                    })}
                  </span>
                </div>

                {/* Company Transition */}
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-medium text-muted-foreground line-through">
                    {alert.old_company || "Previous Company"}
                  </span>
                  <ArrowRight className="size-3 text-rose-500 shrink-0" />
                  <span className="font-bold text-foreground bg-rose-500/10 text-rose-700 dark:text-rose-300 px-2 py-0.5 rounded-md">
                    {alert.new_company}
                  </span>
                  {alert.new_title && (
                    <span className="text-muted-foreground text-[11px]">
                      ({alert.new_title})
                    </span>
                  )}
                </div>

                {/* Re-engagement Hook */}
                {alert.ai_reengagement_hook && (
                  <div className="rounded-lg border bg-background/80 p-2.5 text-xs text-foreground italic leading-relaxed">
                    "{alert.ai_reengagement_hook}"
                  </div>
                )}

                {/* Card Actions */}
                <div className="flex items-center justify-between pt-1">
                  <div className="flex items-center gap-1.5">
                    <Button
                      type="button"
                      size="sm"
                      variant="default"
                      onClick={() => handleCopyAndDM(alert)}
                      className="h-7 text-xs gap-1.5 bg-rose-600 hover:bg-rose-700 text-white cursor-pointer"
                    >
                      {copiedId === alert.id ? (
                        <>
                          <Check className="size-3" />
                          <span>Copied Hook!</span>
                        </>
                      ) : (
                        <>
                          <Send className="size-3" />
                          <span>Copy Hook & DM on LinkedIn</span>
                          <ExternalLink className="size-2.5 opacity-75" />
                        </>
                      )}
                    </Button>
                  </div>

                  {!alert.acknowledged && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => handleAcknowledge(alert.id)}
                      className="h-7 text-xs text-muted-foreground hover:text-foreground cursor-pointer"
                    >
                      <Check className="size-3 mr-1" />
                      Mark Reviewed
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        <DialogFooter className="border-t pt-3 flex items-center justify-between sm:justify-between w-full">
          <div className="text-xs text-muted-foreground flex items-center gap-1.5">
            <Sparkles className="size-3.5 text-rose-500" />
            <span>Champions are tracked weekly for career shifts</span>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
